import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { PawPrint, Droplets, Armchair } from "lucide-react";

const WALL = [
  { video: "/dog-gelato-video-web.mp4", alt: "A dog enjoying a Morelli's dog gelato", caption: "Gone in seconds" },
  { video: "/dog-gelato-video-2.mp4", poster: "/dog-video-2-poster.jpg", alt: "Another happy dog with a Morelli's dog gelato", caption: "Encore!" },
  { src: "/dog-closeup.webp", alt: "Close-up of a golden retriever licking a Morelli's dog gelato cup clean", caption: "Nose-first approval" },
  { src: "/dog-gelato-customer.jpg", alt: "A golden retriever enjoying a Morelli's dog gelato in the parlour", caption: "Loving every lick" },
];

const PERKS = [
  { icon: PawPrint, text: "Dog gelato made in-house — a dog-safe, dairy-free recipe" },
  { icon: Droplets, text: "Fresh water bowls always waiting at the door" },
  { icon: Armchair, text: "Dogs welcome to sit inside the parlour with you" },
];

const DogFriendly = () => (
  <section id="dogs" data-testid="dog-friendly-section" className="py-28 md:py-36 bg-mint/60 relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-6 relative order-2 lg:order-1">
        <Reveal className="flex justify-center lg:justify-start">
          <div className="rounded-t-full rounded-b-[2rem] overflow-hidden border-4 border-white shadow-2xl shadow-teal/20 w-72 md:w-96 aspect-[3/4]">
            <img src="/dog-gelato-customer.jpg" alt="A golden retriever enjoying a Morelli's dog gelato" data-testid="dog-image" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-6 order-1 lg:order-2">
        <SectionHeading overline="Dog Friendly" title="Four Legs Always Welcome" />
        <Reveal delay={0.1}>
          <p className="mt-10 text-base md:text-lg leading-relaxed text-teal/80 max-w-xl">
            A seaside day out isn&rsquo;t complete without your best friend. Dogs are
            welcome throughout the parlour — and they don&rsquo;t have to watch you
            enjoy your sundae empty-pawed.
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <div data-testid="dog-gelato-card" className="mt-8 bg-white rounded-[2rem] p-6 md:p-8 border border-gold/30 shadow-lg shadow-teal/10 flex items-center gap-6 max-w-xl">
            <span className="shrink-0 w-16 h-16 rounded-full bg-mint grid place-items-center">
              <PawPrint size={30} className="text-teal" strokeWidth={1.8} aria-hidden="true" />
            </span>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display font-bold text-2xl text-teal">Morelli&rsquo;s Dog Gelato</h3>
                <span className="bg-gold text-teal-deep text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full font-semibold">£1.00</span>
              </div>
              <p className="text-sm text-teal/70 mt-1 leading-relaxed">Our own dog-safe gelato, churned dairy-free just for them. Tail wags guaranteed.</p>
            </div>
          </div>
        </Reveal>
        <div className="mt-8 flex flex-col gap-4 max-w-xl">
          {PERKS.map((p, i) => (
            <Reveal key={p.text} delay={0.22 + i * 0.06}>
              <div data-testid={`dog-perk-${i}`} className="flex items-center gap-4">
                <span className="shrink-0 w-10 h-10 rounded-full border border-teal/25 grid place-items-center text-teal">
                  <p.icon size={17} strokeWidth={1.8} />
                </span>
                <p className="text-sm text-teal/75">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 md:px-10 mt-24">
      <Reveal className="text-center">
        <p className="text-xs uppercase tracking-[0.35em] text-teal/70 font-medium">The Regulars</p>
        <h3 className="font-display font-bold text-3xl md:text-4xl text-teal mt-3">Four-Legged Guests of Honour</h3>
      </Reveal>
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
        {WALL.map((p, i) => (
          <Reveal key={p.src} delay={i * 0.06}>
            <figure data-testid={`dog-wall-${i}`} className="group relative overflow-hidden rounded-[1.5rem] shadow-md shadow-teal/10 aspect-square">
              {p.video ? (
                <video src={p.video} autoPlay muted loop playsInline preload="auto" poster={p.poster || "/dog-gelato-customer.jpg"} aria-label={p.alt}
                  data-testid={`dog-wall-video-${i}`}
                  onCanPlay={(e) => e.target.play().catch(() => {})}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              ) : (
                <img src={p.src} alt={p.alt} loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              )}
              <figcaption className="absolute inset-0 bg-teal/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
                <span className="font-display italic text-cream text-sm">{p.caption}</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default DogFriendly;
